import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { assertPresent, isNotNullOrUndefined } from '../../../core/utils/typeUtils';
import { CAMPAIGN_BASIC_INSURANCES, CAMPAIGN_PRODUCT_PARAMETER_NAME } from './campaign.product.types';
import { Store } from '@ngrx/store';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
  dateOfBirthPatternValidator,
  firstNamePatternValidator,
  personValidator,
} from '../../../core/utils/validators';
import { BasicInsuranceModelType, Franchise, GenderOrPrenatal } from '../../../core/api';
import { mapToBasicInsuranceModelType, mapToSupplementaryInsuranceType } from './campaign.mapper';
import { AddressState, OfferState, SupplementaryInsuranceTypes } from '../../../core/states/offer/offer.state';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DateService } from '../../../core/services/date.service';
import { addPerson, removePerson, updateOfferDetails, updatePerson } from '../../../core/states/offer/offer.actions';
import {
  getDefaultFranchise,
  hasDefaultAccidentCoverage,
  INITIAL_ACCIDENT_COVERAGE,
  INITIAL_FRANCHISE,
} from '../../../core/utils/insuranceUtils';
import { getPlanForModel } from '../../../core/models/insurance-model-plan-mapping';
import { CampaignFormData, CampaignFormGroup } from './campaign-page.forms';
import { FlyoutOption } from 'dap-ui-library';
import { Address } from '../../key-data/key-data.types';
import { combineLatestWith, Observable, of, skipWhile } from 'rxjs';
import { PersonDataFormGroup } from '../../../shared/person-data/person-data.forms';
import { delay, first } from 'rxjs/operators';
import { getOfferState } from '../../../core/states/offer/offer.selector';
import { removeBasicInsuranceForPersonIntent } from '../../../core/states/offer/basic-insurance/actions';

@UntilDestroy()
@Component({
  standalone: false,
  selector: 'ssc-campaign-page',
  templateUrl: './campaign-page.component.html',
  styleUrls: ['./campaign-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CampaignPageComponent implements OnInit {
  public basicInsuranceProduct: BasicInsuranceModelType;
  public supplementaryInsuranceProduct: SupplementaryInsuranceTypes;
  public formGroup: CampaignFormGroup = new FormGroup(
    {
      gender: new FormControl<GenderOrPrenatal | null>(null, Validators.required),
      dateOfBirth: new FormControl<string>('', {
        nonNullable: true,
        validators: [Validators.required, dateOfBirthPatternValidator],
      }),
      firstName: new FormControl<string>('', {
        nonNullable: true,
        validators: firstNamePatternValidator,
      }),
      address: new FormControl<FlyoutOption<Address> | null>(null, Validators.required),
      saleOfferBegin: new FormControl<string>(this.getSaleOfferBeginDate(), {
        nonNullable: true,
        validators: Validators.required,
      }),
      existingCustomer: new FormControl<boolean | null>(null),
    },
    personValidator,
  );
  public basicInsuranceSelected: boolean;
  public franchise: Franchise = INITIAL_FRANCHISE;
  public includesAccidentCoverage: boolean = INITIAL_ACCIDENT_COVERAGE;
  public showValidations: boolean = false;
  public scrollToErrorEventEmitter: EventEmitter<void> = new EventEmitter();
  private personIdFromState: number | undefined = undefined;

  private formValuesInitialised$: Observable<boolean> = of(false);

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private store: Store,
    private cdr: ChangeDetectorRef,
  ) {}

  public ngOnInit(): void {
    // The Campaign Guard already validates the product and assures that the passed product is of type CampaignProduct.
    // Source: src/app/routing/guards/campaign/campaign.guard.ts
    const selectedProduct = assertPresent(
      this.route.snapshot.paramMap.get(CAMPAIGN_PRODUCT_PARAMETER_NAME),
      'route param CAMPAIGN_PRODUCT_PARAMETER_NAME',
    ) as never;

    this.basicInsuranceSelected = CAMPAIGN_BASIC_INSURANCES.includes(selectedProduct);

    if (this.basicInsuranceSelected) {
      this.basicInsuranceProduct = mapToBasicInsuranceModelType[selectedProduct];
    } else {
      this.supplementaryInsuranceProduct = mapToSupplementaryInsuranceType[selectedProduct];
    }

    this.formGroup.controls.dateOfBirth.valueChanges.pipe(delay(300), untilDestroyed(this)).subscribe((value) => {
      if (value.length === 10) {
        this.franchise = getDefaultFranchise(
          DateService.parseDateFromView(this.formGroup.controls.dateOfBirth.value),
          this.formGroup.controls.saleOfferBegin.value,
        );
        this.includesAccidentCoverage = hasDefaultAccidentCoverage(
          DateService.parseDateFromView(this.formGroup.controls.dateOfBirth.value),
          this.formGroup.controls.saleOfferBegin.value,
        );
        this.cdr.detectChanges();
      }
    });

    this.store
      .select(getOfferState)
      .pipe(first(), untilDestroyed(this))
      .subscribe((offerState) => {
        this.patchForm(offerState);
      });

    this.formValuesInitialised$
      .pipe(
        skipWhile((initialised) => !initialised),
        first(),
        delay(300),
        untilDestroyed(this),
      )
      .subscribe(() => this.initFormEventListeners());
  }

  private initFormEventListeners(): void {
    const setDefaultFranchiseAndAccidentCoverage = (dateOfBirth: string, saleOfferBegin: string): void => {
      this.franchise = getDefaultFranchise(DateService.parseDateFromView(dateOfBirth), saleOfferBegin);
      this.includesAccidentCoverage = hasDefaultAccidentCoverage(
        DateService.parseDateFromView(dateOfBirth),
        saleOfferBegin,
      );
    };

    if (this.basicInsuranceSelected) {
      const personForm = this.campaignFormGroup();
      if (isNotNullOrUndefined(this.personIdFromState)) {
        personForm.controls.dateOfBirth.valueChanges.pipe(untilDestroyed(this)).subscribe((dateOfBirth) => {
          setDefaultFranchiseAndAccidentCoverage(dateOfBirth, personForm.controls.saleOfferBegin.value);
        });
      } else {
        personForm.controls.dateOfBirth.valueChanges
          .pipe(combineLatestWith(personForm.controls.saleOfferBegin.valueChanges), untilDestroyed(this))
          .subscribe(([dateOfBirth, saleOfferBegin]) => {
            setDefaultFranchiseAndAccidentCoverage(dateOfBirth, saleOfferBegin);
          });
      }
    }
  }

  public personFormGroup(): PersonDataFormGroup {
    return this.formGroup as FormGroup;
  }

  public campaignFormGroup(): CampaignFormGroup {
    return this.formGroup as FormGroup;
  }

  public tryUpdateStoreAndNavigate(): void {
    if (!this.validateForm()) {
      return;
    }

    const formData = this.formGroup.value as CampaignFormData;
    this.store.dispatch(
      updateOfferDetails({
        address: formData.address.value,
        saleOfferBegin: formData.saleOfferBegin,
        existingCustomer: formData.existingCustomer,
      }),
    );
    this.store.dispatch(
      isNotNullOrUndefined(this.personIdFromState)
        ? updatePerson({
            personId: this.personIdFromState,
            details: {
              firstName: formData.firstName || undefined,
              dateOfBirth: DateService.parseDateFromView(formData.dateOfBirth),
              genderOrPrenatal: formData.gender,
            },
          })
        : addPerson({
            details: {
              firstName: formData.firstName || undefined,
              dateOfBirth: DateService.parseDateFromView(formData.dateOfBirth),
              genderOrPrenatal: formData.gender,
            },
          }),
    );

    if (this.basicInsuranceSelected) {
      this.router.navigate(['basic-insurance', 0], {
        queryParams: {
          product: this.basicInsuranceProduct,
          franchise: this.franchise,
          includesAccidentCoverage: this.includesAccidentCoverage,
        },
        fragment: getPlanForModel(this.basicInsuranceProduct),
      });
    } else {
      this.router.navigate([`supplementary-insurance`, 0], {
        queryParams: { product: this.supplementaryInsuranceProduct },
        fragment: this.supplementaryInsuranceProduct,
      });
    }
  }

  public onAccidentCoverageSelected(coverage: boolean): void {
    this.includesAccidentCoverage = coverage;
  }

  public onFranchiseSelected(franchise: Franchise): void {
    this.franchise = franchise;
  }

  private validateForm(): boolean {
    const isValid = !this.formGroup.invalid;
    if (!isValid) {
      this.showValidations = true;
      this.scrollToErrorEventEmitter.emit();
    }
    return isValid;
  }

  private patchForm({ persons, address, saleOfferBegin, existingCustomer }: OfferState): void {
    if (persons.length > 0 && address !== undefined) {
      persons.forEach((person) => {
        if (!isNotNullOrUndefined(this.personIdFromState) || this.personIdFromState === person.personId) {
          this.campaignFormGroup().patchValue(
            {
              gender: person.details.genderOrPrenatal,
              dateOfBirth: DateService.formatDateForView(person.details.dateOfBirth),
              firstName: person.details.firstName,
              address: CampaignPageComponent.mapToAddressOption(address),
              saleOfferBegin,
              existingCustomer,
            },
            { emitEvent: false, onlySelf: true },
          );

          if (this.basicInsuranceSelected) {
            if (person?.basicInsurance?.franchise) {
              this.franchise = person.basicInsurance.franchise;
            }
            if (person?.basicInsurance?.includesAccidentCoverage) {
              this.includesAccidentCoverage = person.basicInsurance.includesAccidentCoverage;
            }
            this.store.dispatch(removeBasicInsuranceForPersonIntent({ personId: person.personId }));
          }
          this.personIdFromState = person.personId;
        } else {
          this.store.dispatch(removePerson({ personId: person.personId }));
        }
      });
    } else {
      this.personIdFromState = undefined;
    }
    this.formValuesInitialised$ = of(true);
  }

  private static mapToAddressOption(address: AddressState): FlyoutOption<Address> {
    return {
      value: {
        bfsNumber: address.bfsNumber,
        zipCode: address.zipCode,
        city: address.city,
      },
      label: `${address.zipCode} ${address.city}`,
    };
  }

  private getSaleOfferBeginDate(): string {
    const date = new Date();
    return `${date.getFullYear() + 1}-01-01`;
  }
}
